<template>
  <section>
    <div class="d-flex flex-wrap align-center mb-2">
      <div>
        <p class="text-subtitle-1 font-weight-medium mb-1">
          PDF-Briefpapier
        </p>
        <p class="text-caption text--secondary mb-0">
          Das Briefpapier gilt für alle neu erzeugten Rechnungen und Angebote – auch
          für automatisch erzeugte geplante Dokumente.
        </p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        small
        text
        color="primary"
        class="mt-2"
        :loading="laedt"
        :disabled="anfrageLaeuft"
        @click="laden"
      >
        <v-icon left>
          mdi-refresh
        </v-icon>
        Neu laden
      </v-btn>
    </div>

    <v-alert
      v-if="nachricht"
      dense
      text
      class="mt-4"
      :type="nachrichtTyp"
    >
      {{ nachricht }}
    </v-alert>

    <v-skeleton-loader
      v-if="laedt && !geladen"
      class="mt-4"
      type="image, article"
    ></v-skeleton-loader>

    <template v-else>
      <v-row class="mt-2">
        <v-col cols="12" md="5">
          <v-card outlined class="overflow-hidden">
            <v-card-text class="pa-3">
              <div class="d-flex flex-wrap align-center mb-3">
                <v-chip
                  small
                  dark
                  :color="status.isCustom ? 'success' : 'grey'"
                >
                  <v-icon left small>
                    {{ status.isCustom ? 'mdi-image-check' : 'mdi-image-outline' }}
                  </v-icon>
                  {{ status.isCustom ? 'Eigenes Bild aktiv' : 'Standardbild aktiv' }}
                </v-chip>
                <v-chip
                  v-if="ausgewaehlteDatei"
                  small
                  outlined
                  color="primary"
                  class="ml-2"
                >
                  Vorschau der Auswahl
                </v-chip>
              </div>

              <div class="background-preview">
                <v-img
                  v-if="angezeigteVorschau"
                  :src="angezeigteVorschau"
                  :aspect-ratio="2480 / 3508"
                  contain
                  alt="Vorschau des PDF-Briefpapiers"
                >
                  <template v-slot:placeholder>
                    <div class="fill-height d-flex align-center justify-center">
                      <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                  </template>
                </v-img>
                <div
                  v-else
                  class="background-preview__empty d-flex flex-column align-center justify-center"
                >
                  <v-icon large color="grey lighten-1">
                    mdi-image-off-outline
                  </v-icon>
                  <span class="text-caption text--secondary mt-2">
                    Keine Vorschau verfügbar
                  </span>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-alert dense text type="info" class="mb-5">
            <strong>Empfehlung:</strong> A4-Hochformat mit
            {{ empfohleneBreite }} × {{ empfohleneHoehe }} Pixeln bei 300 dpi,
            JPEG oder PNG, maximal {{ maximaleGroesse }} und {{ maximaleMegapixel }}.
            Breite höchstens {{ maximaleBreite }} Pixel, Höhe höchstens {{ maximaleHoehe }} Pixel.
          </v-alert>

          <v-row dense class="mb-3">
            <v-col cols="12" sm="6">
              <span class="text-caption text--secondary">Aktives Briefpapier</span><br>
              <span>
                {{ status.isCustom ? 'Individuelles Bild' : 'Mitgeliefertes Standardbild' }}
              </span>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="text-caption text--secondary">Bildgröße</span><br>
              <span>{{ statusAbmessungen }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="text-caption text--secondary">Dateigröße</span><br>
              <span>{{ dateigroesse(status.size) }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <span class="text-caption text--secondary">Zuletzt geändert</span><br>
              <span>{{ datumZeit(status.updatedAt) }}</span>
            </v-col>
          </v-row>

          <v-form
            ref="uploadFormular"
            v-model="formularGueltig"
            @submit.prevent="speichern"
          >
            <v-file-input
              v-model="ausgewaehlteDatei"
              accept="image/jpeg,image/png,.jpg,.jpeg,.png"
              label="Neues Briefpapier auswählen"
              prepend-icon="mdi-image-plus"
              show-size
              :disabled="anfrageLaeuft"
              :rules="dateiRegeln"
              :hint="uploadHinweis"
              persistent-hint
              @change="dateiGeaendert"
            ></v-file-input>
          </v-form>

          <v-chip
            v-if="auswahlAbmessungen"
            small
            outlined
            class="mt-3 status-chip"
            :color="auswahlStatusFarbe"
          >
            <v-icon left small>
              {{ auswahlStatusIcon }}
            </v-icon>
            {{ auswahlStatusText }}
          </v-chip>

          <div class="d-flex flex-wrap justify-end mt-5">
            <v-btn
              v-if="status.isCustom"
              small
              outlined
              color="warning"
              class="mr-2 mb-2"
              :disabled="anfrageLaeuft || !status.revision"
              @click="resetDialog = true"
            >
              <v-icon left>
                mdi-restore
              </v-icon>
              Standardbild wiederherstellen
            </v-btn>
            <v-btn
              small
              color="success"
              class="mb-2"
              :loading="speichert"
              :disabled="!speichernMoeglich"
              @click="speichern"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              Briefpapier speichern
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <v-dialog
      ref="resetDialogComponent"
      v-model="resetDialog"
      persistent
      max-width="520"
    >
      <v-card>
        <v-card-title id="invoice-background-reset-title" class="warning white--text">
          Standardbild wiederherstellen?
        </v-card-title>
        <v-card-text class="pt-5">
          Das individuelle PDF-Briefpapier wird entfernt. Neue Rechnungen und Angebote
          verwenden danach wieder das mitgelieferte Standardbild. Bereits erzeugte PDFs
          bleiben unverändert.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="setztZurueck"
            @click="resetDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="warning"
            dark
            :loading="setztZurueck"
            @click="zuruecksetzen"
          >
            Standardbild verwenden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
const STANDARD_BREITE = 2480;
const STANDARD_HOEHE = 3508;
const STANDARD_MAX_BYTES = 10 * 1024 * 1024;
const STANDARD_MAX_PIXEL = 10000000;
const STANDARD_MAX_BREITE = 6000;
const STANDARD_MAX_HOEHE = 6000;
const STANDARD_DATEITYPEN = ['image/jpeg', 'image/png'];

function leererStatus() {
  return {
    source: 'default',
    isCustom: false,
    width: 0,
    height: 0,
    size: 0,
    updatedAt: null,
    revision: '',
    recommendedWidth: STANDARD_BREITE,
    recommendedHeight: STANDARD_HOEHE,
    maxBytes: STANDARD_MAX_BYTES,
    maxWidth: STANDARD_MAX_BREITE,
    maxHeight: STANDARD_MAX_HOEHE,
    maxPixels: STANDARD_MAX_PIXEL,
    acceptedTypes: [...STANDARD_DATEITYPEN],
  };
}

export default {
  name: 'RechnungshintergrundEinstellungen',
  props: {
    propStatus: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    status: leererStatus(),
    geladen: false,
    laedt: false,
    speichert: false,
    setztZurueck: false,
    formularGueltig: false,
    ausgewaehlteDatei: null,
    auswahlBreite: 0,
    auswahlHoehe: 0,
    auswahlWirdGeprueft: false,
    auswahlBildLesbar: false,
    aktuelleVorschau: '',
    auswahlVorschau: '',
    nachricht: '',
    nachrichtTyp: 'info',
    resetDialog: false,
    uploadHinweis: 'Das bisherige Bild bleibt aktiv, bis das neue Bild '
      + 'erfolgreich gespeichert wurde.',
    aktiveController: [],
    bildPruefung: 0,
    wirdZerstoert: false,
  }),
  computed: {
    anfrageLaeuft() {
      return this.laedt || this.speichert || this.setztZurueck;
    },
    angezeigteVorschau() {
      return this.auswahlVorschau || this.aktuelleVorschau;
    },
    empfohleneBreite() {
      return Number(this.status.recommendedWidth) || STANDARD_BREITE;
    },
    empfohleneHoehe() {
      return Number(this.status.recommendedHeight) || STANDARD_HOEHE;
    },
    maxBytes() {
      return Number(this.status.maxBytes) || STANDARD_MAX_BYTES;
    },
    akzeptierteTypen() {
      return Array.isArray(this.status.acceptedTypes) && this.status.acceptedTypes.length
        ? this.status.acceptedTypes : STANDARD_DATEITYPEN;
    },
    maximaleGroesse() {
      return this.dateigroesse(this.maxBytes);
    },
    maxPixels() {
      return Number(this.status.maxPixels) || STANDARD_MAX_PIXEL;
    },
    maximaleBreite() {
      return Number(this.status.maxWidth) || STANDARD_MAX_BREITE;
    },
    maximaleHoehe() {
      return Number(this.status.maxHeight) || STANDARD_MAX_HOEHE;
    },
    maximaleMegapixel() {
      return `${(this.maxPixels / 1000000).toLocaleString('de-DE')} Megapixel`;
    },
    statusAbmessungen() {
      const breite = Number(this.status.width);
      const hoehe = Number(this.status.height);
      return breite > 0 && hoehe > 0 ? `${breite} × ${hoehe} px` : 'Nicht verfügbar';
    },
    auswahlAbmessungen() {
      return this.auswahlBreite > 0 && this.auswahlHoehe > 0
        ? `${this.auswahlBreite} × ${this.auswahlHoehe} px` : '';
    },
    auswahlHatEmpfohleneAbmessungen() {
      return this.auswahlBreite === this.empfohleneBreite
        && this.auswahlHoehe === this.empfohleneHoehe;
    },
    auswahlUeberschreitetPixelgrenze() {
      return this.auswahlBreite * this.auswahlHoehe > this.maxPixels;
    },
    auswahlUeberschreitetKantengrenze() {
      return this.auswahlBreite > this.maximaleBreite
        || this.auswahlHoehe > this.maximaleHoehe;
    },
    auswahlStatusFarbe() {
      if (this.auswahlUeberschreitetKantengrenze
        || this.auswahlUeberschreitetPixelgrenze) return 'error';
      return this.auswahlHatEmpfohleneAbmessungen ? 'success' : 'warning';
    },
    auswahlStatusIcon() {
      if (this.auswahlUeberschreitetKantengrenze
        || this.auswahlUeberschreitetPixelgrenze) return 'mdi-alert-circle';
      return this.auswahlHatEmpfohleneAbmessungen
        ? 'mdi-check-circle' : 'mdi-alert-circle-outline';
    },
    auswahlStatusText() {
      if (this.auswahlUeberschreitetKantengrenze) {
        return `Ausgewählt: ${this.auswahlAbmessungen} – eine Bildkante ist zu lang`;
      }
      if (this.auswahlUeberschreitetPixelgrenze) {
        return `Ausgewählt: ${this.auswahlAbmessungen} – die Pixelgrenze wird überschritten`;
      }
      if (this.auswahlHatEmpfohleneAbmessungen) {
        return `Ausgewählt: ${this.auswahlAbmessungen} – empfohlene Auflösung`;
      }
      return `Ausgewählt: ${this.auswahlAbmessungen}`;
    },
    dateiRegeln() {
      return [
        (datei) => !datei || this.istErlaubterDateityp(datei)
          || 'Bitte eine JPEG- oder PNG-Datei auswählen.',
        (datei) => !datei || datei.size <= this.maxBytes
          || `Die Datei darf höchstens ${this.maximaleGroesse} groß sein.`,
        () => !this.auswahlAbmessungen || !this.auswahlUeberschreitetPixelgrenze
          || `Das Bild darf höchstens ${this.maximaleMegapixel} enthalten.`,
        () => !this.auswahlAbmessungen || !this.auswahlUeberschreitetKantengrenze
          || `Breite und Höhe dürfen höchstens ${this.maximaleBreite} × `
            + `${this.maximaleHoehe} Pixel betragen.`,
      ];
    },
    speichernMoeglich() {
      return Boolean(this.ausgewaehlteDatei)
        && this.formularGueltig
        && this.istErlaubterDateityp(this.ausgewaehlteDatei)
        && this.ausgewaehlteDatei.size <= this.maxBytes
        && this.auswahlBildLesbar
        && !this.auswahlWirdGeprueft
        && !this.auswahlUeberschreitetKantengrenze
        && !this.auswahlUeberschreitetPixelgrenze
        && Boolean(this.status.revision)
        && !this.anfrageLaeuft;
    },
  },
  watch: {
    propStatus: {
      handler(status) {
        if (status && typeof status === 'object') this.statusUebernehmen(status);
      },
      deep: true,
      immediate: true,
    },
    resetDialog(geoeffnet) {
      if (geoeffnet) this.dialogBeschriften();
    },
  },
  methods: {
    statusUebernehmen(status) {
      this.status = {
        ...leererStatus(),
        ...status,
        isCustom: status.isCustom === true || status.source === 'custom',
        acceptedTypes: Array.isArray(status.acceptedTypes)
          ? status.acceptedTypes : STANDARD_DATEITYPEN,
      };
      this.geladen = true;
    },
    istErlaubterDateityp(datei) {
      if (!datei) return false;
      if (this.akzeptierteTypen.includes(String(datei.type || '').toLowerCase())) return true;
      if (datei.type) return false;
      return /\.(jpe?g|png)$/i.test(String(datei.name || ''));
    },
    setzeNachricht(typ, text) {
      this.nachrichtTyp = typ;
      this.nachricht = text;
    },
    controllerAnlegen() {
      if (typeof AbortController === 'undefined') return null;
      const controller = new AbortController();
      this.aktiveController.push(controller);
      return controller;
    },
    controllerEntfernen(controller) {
      this.aktiveController = this.aktiveController.filter((item) => item !== controller);
    },
    async anfrage(aktion, datei = null) {
      if (this.wirdZerstoert) return null;
      const formular = new FormData();
      formular.append('code', this.$store.state.login.session.code);
      formular.append('action', aktion);
      if (datei) formular.append('invoiceBackground', datei);
      if ((aktion === 'save' || aktion === 'reset') && this.status.revision !== '') {
        formular.append('expectedRevision', String(this.status.revision));
      }
      const controller = this.controllerAnlegen();

      let response;
      try {
        response = await fetch(
          `${process.env.VUE_APP_API_HOST}/400/410/invoice_background.php`,
          {
            method: 'POST',
            body: formular,
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            signal: controller ? controller.signal : undefined,
          },
        );
      } catch (error) {
        if (error && error.name === 'AbortError' && this.wirdZerstoert) return null;
        throw new Error('Der Server konnte nicht erreicht werden. Bitte erneut versuchen.');
      } finally {
        this.controllerEntfernen(controller);
      }

      let ergebnis;
      try {
        ergebnis = await response.json();
      } catch {
        throw new Error('Der Server hat keine gültige Antwort geliefert.');
      }
      if (this.wirdZerstoert) return null;
      if (ergebnis && ergebnis.status === 'accessError') {
        this.$store.commit('login/backendErrorSession');
        throw new Error('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.');
      }
      if (response.status === 409 || (ergebnis && ergebnis.status === 'conflict')) {
        if (ergebnis.invoiceBackground && typeof ergebnis.invoiceBackground === 'object') {
          this.statusUebernehmen(ergebnis.invoiceBackground);
        }
        const konflikt = new Error(
          (ergebnis && ergebnis.message)
          || `Das PDF-Briefpapier wurde zwischenzeitlich geändert.
            Bitte prüfen Sie den aktuellen Stand und führen Sie die Aktion erneut aus.`,
        );
        konflikt.konflikt = true;
        throw konflikt;
      }
      if (!response.ok || !ergebnis || ergebnis.status !== 'success') {
        throw new Error(
          (ergebnis && ergebnis.message)
          || 'Das PDF-Briefpapier konnte nicht verarbeitet werden.',
        );
      }
      if (!ergebnis.invoiceBackground || typeof ergebnis.invoiceBackground !== 'object') {
        throw new Error('Der Server hat unvollständige Hintergrunddaten geliefert.');
      }
      this.statusUebernehmen(ergebnis.invoiceBackground);
      return ergebnis;
    },
    async vorschauLaden() {
      if (this.wirdZerstoert) return;
      const formular = new FormData();
      formular.append('code', this.$store.state.login.session.code);
      formular.append('action', 'preview');
      const controller = this.controllerAnlegen();
      let response;
      try {
        response = await fetch(
          `${process.env.VUE_APP_API_HOST}/400/410/invoice_background.php`,
          {
            method: 'POST',
            body: formular,
            cache: 'no-store',
            headers: { Accept: 'image/jpeg, application/json' },
            signal: controller ? controller.signal : undefined,
          },
        );
      } catch (error) {
        if (error && error.name === 'AbortError' && this.wirdZerstoert) return;
        throw new Error('Die Vorschau konnte nicht geladen werden.');
      } finally {
        this.controllerEntfernen(controller);
      }

      if (!response.ok || String(response.headers.get('content-type') || '').includes('json')) {
        let ergebnis = null;
        try {
          ergebnis = await response.json();
        } catch {
          // Eine fehlerhafte JSON-Antwort wird mit einer allgemeinen Meldung behandelt.
        }
        if (ergebnis && ergebnis.status === 'accessError') {
          this.$store.commit('login/backendErrorSession');
        }
        throw new Error(
          (ergebnis && ergebnis.message) || 'Die Vorschau konnte nicht geladen werden.',
        );
      }

      const bild = await response.blob();
      if (this.wirdZerstoert) return;
      if (!bild.size || !String(bild.type || '').startsWith('image/')) {
        throw new Error('Der Server hat keine gültige Bildvorschau geliefert.');
      }
      const neueVorschau = URL.createObjectURL(bild);
      this.urlFreigeben(this.aktuelleVorschau);
      this.aktuelleVorschau = neueVorschau;
    },
    async laden() {
      if (this.anfrageLaeuft) return;
      if (this.ausgewaehlteDatei) this.auswahlZuruecksetzen();
      this.laedt = true;
      this.nachricht = '';
      try {
        await this.anfrage('status');
        if (this.wirdZerstoert) return;
        await this.vorschauLaden();
      } catch (error) {
        if (!this.wirdZerstoert) {
          this.setzeNachricht(
            'error',
            error.message || 'Das PDF-Briefpapier konnte nicht geladen werden.',
          );
        }
      } finally {
        this.laedt = false;
      }
    },
    async konfliktBehandeln(error) {
      if (!error || !error.konflikt) return false;
      const meldung = error.message;
      this.resetDialog = false;
      try {
        await this.statusUndVorschauNeuLaden();
        this.setzeNachricht('warning', meldung);
      } catch {
        this.setzeNachricht(
          'error',
          `${meldung} Der aktuelle Stand konnte anschließend nicht geladen werden.`,
        );
      }
      return true;
    },
    async statusUndVorschauNeuLaden() {
      await this.anfrage('status');
      if (this.wirdZerstoert) return;
      await this.vorschauLaden();
    },
    async speichern() {
      if (!this.$refs.uploadFormular || !this.$refs.uploadFormular.validate()) return;
      if (!this.speichernMoeglich) return;
      this.speichert = true;
      this.nachricht = '';
      try {
        const ergebnis = await this.anfrage('save', this.ausgewaehlteDatei);
        if (this.wirdZerstoert) return;
        this.auswahlZuruecksetzen();
        let vorschauFehler = '';
        try {
          await this.vorschauLaden();
        } catch (error) {
          vorschauFehler = error.message || 'Die Vorschau konnte nicht aktualisiert werden.';
        }
        this.setzeNachricht(
          vorschauFehler ? 'warning' : 'success',
          vorschauFehler
            ? `${ergebnis.message || 'Das PDF-Briefpapier wurde gespeichert.'} ${vorschauFehler}`
            : ergebnis.message || 'Das PDF-Briefpapier wurde gespeichert.',
        );
      } catch (error) {
        if (!this.wirdZerstoert && !(await this.konfliktBehandeln(error))) {
          this.setzeNachricht(
            'error',
            error.message || 'Das PDF-Briefpapier konnte nicht gespeichert werden.',
          );
        }
      } finally {
        this.speichert = false;
      }
    },
    async zuruecksetzen() {
      if (this.setztZurueck) return;
      this.setztZurueck = true;
      this.nachricht = '';
      try {
        const ergebnis = await this.anfrage('reset');
        if (this.wirdZerstoert) return;
        this.auswahlZuruecksetzen();
        this.resetDialog = false;
        let vorschauFehler = '';
        try {
          await this.vorschauLaden();
        } catch (error) {
          vorschauFehler = error.message || 'Die Vorschau konnte nicht aktualisiert werden.';
        }
        this.setzeNachricht(
          vorschauFehler ? 'warning' : 'success',
          vorschauFehler
            ? `${ergebnis.message || 'Das Standardbild ist wieder aktiv.'} ${vorschauFehler}`
            : ergebnis.message || 'Das Standardbild ist wieder aktiv.',
        );
      } catch (error) {
        if (!this.wirdZerstoert && !(await this.konfliktBehandeln(error))) {
          this.setzeNachricht(
            'error',
            error.message || 'Das Standardbild konnte nicht wiederhergestellt werden.',
          );
        }
      } finally {
        this.setztZurueck = false;
      }
    },
    dateiGeaendert(datei) {
      this.bildPruefung += 1;
      const pruefung = this.bildPruefung;
      this.urlFreigeben(this.auswahlVorschau);
      this.auswahlVorschau = '';
      this.auswahlBreite = 0;
      this.auswahlHoehe = 0;
      this.auswahlWirdGeprueft = false;
      this.auswahlBildLesbar = false;
      if (!datei || !this.istErlaubterDateityp(datei) || datei.size > this.maxBytes) return;

      const lokaleVorschau = URL.createObjectURL(datei);
      this.auswahlVorschau = lokaleVorschau;
      this.auswahlWirdGeprueft = true;
      const bild = new Image();
      bild.onload = () => {
        if (pruefung !== this.bildPruefung) return;
        this.auswahlBreite = Number(bild.naturalWidth) || 0;
        this.auswahlHoehe = Number(bild.naturalHeight) || 0;
        this.auswahlBildLesbar = this.auswahlBreite > 0 && this.auswahlHoehe > 0;
        this.auswahlWirdGeprueft = false;
        this.$nextTick(() => {
          if (this.$refs.uploadFormular) this.$refs.uploadFormular.validate();
        });
      };
      bild.onerror = () => {
        if (pruefung !== this.bildPruefung) return;
        this.auswahlWirdGeprueft = false;
        this.auswahlBildLesbar = false;
        this.setzeNachricht('warning', 'Die lokale Bildvorschau konnte nicht gelesen werden.');
        this.$nextTick(() => {
          if (this.$refs.uploadFormular) this.$refs.uploadFormular.validate();
        });
      };
      bild.src = lokaleVorschau;
    },
    auswahlZuruecksetzen() {
      this.bildPruefung += 1;
      this.ausgewaehlteDatei = null;
      this.auswahlBreite = 0;
      this.auswahlHoehe = 0;
      this.auswahlWirdGeprueft = false;
      this.auswahlBildLesbar = false;
      this.urlFreigeben(this.auswahlVorschau);
      this.auswahlVorschau = '';
      this.$nextTick(() => {
        if (this.$refs.uploadFormular) this.$refs.uploadFormular.resetValidation();
      });
    },
    urlFreigeben(url) {
      if (url) URL.revokeObjectURL(url);
    },
    dateigroesse(bytes) {
      const groesse = Number(bytes);
      if (!Number.isFinite(groesse) || groesse <= 0) return 'Nicht verfügbar';
      if (groesse >= 1024 * 1024) {
        const megabytes = (groesse / (1024 * 1024)).toLocaleString(
          'de-DE',
          { maximumFractionDigits: 1 },
        );
        return `${megabytes} MB`;
      }
      if (groesse >= 1024) return `${Math.round(groesse / 1024).toLocaleString('de-DE')} KB`;
      return `${groesse.toLocaleString('de-DE')} Byte`;
    },
    datumZeit(wert) {
      if (!wert) return this.status.isCustom ? 'Nicht verfügbar' : 'Mit der App ausgeliefert';
      const timestamp = Number(wert);
      const datum = new Date(timestamp < 1000000000000 ? timestamp * 1000 : timestamp);
      if (Number.isNaN(datum.getTime())) return 'Nicht verfügbar';
      return datum.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
    },
    dialogBeschriften() {
      this.$nextTick(() => {
        const dialog = this.$refs.resetDialogComponent;
        const inhalt = dialog && dialog.$refs ? dialog.$refs.content : null;
        if (inhalt) inhalt.setAttribute('aria-labelledby', 'invoice-background-reset-title');
      });
    },
  },
  created() {
    this.laden();
  },
  beforeDestroy() {
    this.wirdZerstoert = true;
    this.aktiveController.forEach((controller) => controller.abort());
    this.aktiveController = [];
    this.urlFreigeben(this.aktuelleVorschau);
    this.urlFreigeben(this.auswahlVorschau);
  },
};
</script>

<style scoped>
.background-preview {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #f5f5f5;
}

.background-preview__empty {
  min-height: 280px;
}

.status-chip {
  height: auto;
  white-space: normal;
}

.status-chip ::v-deep .v-chip__content {
  padding-top: 6px;
  padding-bottom: 6px;
  white-space: normal;
}
</style>
